/**
 * PromptDetailDialog component
 * Modal dialog showing prompt details with Content, History, and Audit tabs
 */
import { formatDistanceToNow } from 'date-fns'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Plus } from 'lucide-react'
import { PromptStatusBadge } from './PromptStatusBadge'
import type { PromptDetail, PromptVersion, PromptAuditRecord } from '@/types/prompts'

interface PromptDetailDialogProps {
  promptName: string | null
  promptDetail: PromptDetail | null
  isLoading: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreateVersion?: () => void
  onActivateVersion?: (version: number) => void
}

/**
 * Loading skeleton for prompt detail
 */
function PromptDetailLoading() {
  return (
    <div data-testid="prompt-detail-loading" className="space-y-4">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-4 w-64" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  )
}

/**
 * Format relative time for display
 */
function formatRelativeTime(dateString: string): string {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true })
  } catch {
    return dateString
  }
}

/**
 * Content tab - displays the current prompt content
 */
function ContentTab({
  promptDetail,
  onCreateVersion,
}: {
  promptDetail: PromptDetail
  onCreateVersion?: () => void
}) {
  return (
    <div className="space-y-4">
      {/* Version info header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            Current Version: v{promptDetail.currentVersion}
          </span>
          <PromptStatusBadge
            status={promptDetail.status}
            experimentalPercentage={promptDetail.experimentalPercentage ?? undefined}
          />
        </div>
        {onCreateVersion && (
          <Button size="sm" onClick={onCreateVersion}>
            <Plus className="mr-2 h-3 w-3" />
            Create New Version
          </Button>
        )}
      </div>

      {/* Content area - scrollable */}
      <ScrollArea className="h-[400px] rounded-md border bg-muted/30 p-4">
        <pre className="whitespace-pre-wrap text-sm font-mono">
          {promptDetail.content}
        </pre>
      </ScrollArea>

      {/* Metadata */}
      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
        {promptDetail.tokenEstimate && (
          <span>Token Estimate: ~{promptDetail.tokenEstimate.toLocaleString()} tokens</span>
        )}
        {promptDetail.contentHash && (
          <span>Hash: {promptDetail.contentHash.slice(0, 8)}...</span>
        )}
      </div>

      <div className="text-sm text-muted-foreground">
        Last updated: {formatRelativeTime(promptDetail.updatedAt)} by {promptDetail.updatedBy}
      </div>
    </div>
  )
}

/**
 * History tab - displays version history
 */
function HistoryTab({
  versions,
  onActivateVersion: _onActivateVersion,
}: {
  versions: PromptVersion[]
  onActivateVersion?: (version: number) => void
}) {
  return (
    <div className="space-y-4">
      <ScrollArea className="h-[400px]">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>By</TableHead>
                <TableHead>Change Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versions.map((version) => (
                <TableRow key={version.version}>
                  <TableCell className="font-medium">v{version.version}</TableCell>
                  <TableCell>
                    <PromptStatusBadge status={version.status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatRelativeTime(version.createdAt)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {version.createdBy}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate text-muted-foreground">
                    {version.changeNote || '-'}
                  </TableCell>
                </TableRow>
              ))}
              {versions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No version history
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </ScrollArea>
    </div>
  )
}

/**
 * Audit tab - displays audit trail
 */
function AuditTab({ auditTrail }: { auditTrail: PromptAuditRecord[] }) {
  return (
    <div className="space-y-4">
      <ScrollArea className="h-[400px]">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>By</TableHead>
                <TableHead>When</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditTrail.map((record, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Badge variant="outline">{record.action}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {record.performedBy}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatRelativeTime(record.performedAt)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {record.fromVersion && record.toVersion && (
                      <span>v{record.fromVersion} → v{record.toVersion}</span>
                    )}
                    {record.experimentPercentage !== null && (
                      <span>{record.experimentPercentage}%</span>
                    )}
                    {record.reason && <span className="ml-2">({record.reason})</span>}
                  </TableCell>
                </TableRow>
              ))}
              {auditTrail.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No audit records
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </ScrollArea>
    </div>
  )
}

export function PromptDetailDialog({
  promptName: _promptName,
  promptDetail,
  isLoading,
  open,
  onOpenChange,
  onCreateVersion,
  onActivateVersion,
}: PromptDetailDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        {isLoading ? (
          <PromptDetailLoading />
        ) : promptDetail ? (
          <>
            <DialogHeader>
              <DialogTitle>{promptDetail.promptName}</DialogTitle>
              <DialogDescription>
                Agent: {promptDetail.agent} • Category: {promptDetail.category}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="content" className="mt-2">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="content">Content</TabsTrigger>
                <TabsTrigger value="history">
                  History ({promptDetail.versions?.length ?? 0})
                </TabsTrigger>
                <TabsTrigger value="audit">Audit Trail</TabsTrigger>
              </TabsList>

              <TabsContent value="content" className="mt-4">
                <ContentTab
                  promptDetail={promptDetail}
                  onCreateVersion={onCreateVersion}
                />
              </TabsContent>

              <TabsContent value="history" className="mt-4">
                <HistoryTab
                  versions={promptDetail.versions ?? []}
                  onActivateVersion={onActivateVersion}
                />
              </TabsContent>

              <TabsContent value="audit" className="mt-4">
                <AuditTab auditTrail={promptDetail.auditTrail ?? []} />
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="flex items-center justify-center h-32">
            <span className="text-muted-foreground">Prompt not found</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
